import { Box, Button, Paper, Typography, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { memo, useCallback, useEffect, useRef } from 'react';
import {
  DEFAULT_ARCHITECTURE,
  DEFAULT_MEMORY,
  DEFAULT_RUNTIME,
  DEFAULT_SNAPSTART_ENABLED,
  DEFAULT_TIMEOUT,
} from 'config-editor-common';
import type { Lambda, LambdaDefaults } from 'config-editor-common';
import { LambdaListEditorRow } from './LambdaListEditorRow';
import { LambdaDefaultsEditor } from './LambdaComponents/phx-api-v2/LambdaDefaultsEditor';
import { LambdaNameInputRef } from './LambdaComponents/shared/LambdaNameInput';

const useStyles = makeStyles(theme => ({
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
}));

// Re-export types for backward compatibility
export type { Lambda, LambdaDefaults };

interface LambdaListEditorProps {
  lambdas: Record<string, Lambda>;
  onChange: (lambdas: Record<string, Lambda>) => void;
  defaults?: LambdaDefaults;
  onDefaultsChange?: (defaults: LambdaDefaults) => void;
  /** IAM policies at group level (not in defaults object) */
  iamPolicyNames?: string[];
  customIamPolicyNames?: string[];
  onIamPolicyNamesChange?: (policies: string[]) => void;
  onCustomIamPolicyNamesChange?: (policies: string[]) => void;
  title?: string;
  description?: string;
  showPaper?: boolean;
  showLambdas?: boolean;
  isAuthorizer?: boolean; // Indicates if this is for authorizer lambdas
  buildNumber?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateAddButtonText?: string;
}

const LambdaListEditorComponent = ({
  lambdas,
  onChange,
  defaults: propsDefaults,
  onDefaultsChange,
  iamPolicyNames = [],
  customIamPolicyNames = [],
  onIamPolicyNamesChange,
  onCustomIamPolicyNamesChange,
  title = 'Lambdas',
  description,
  showPaper = true,
  showLambdas = true,
  isAuthorizer = false,
  buildNumber = true,
  emptyStateTitle,
  emptyStateDescription,
  emptyStateAddButtonText = 'Add First Lambda',
}: LambdaListEditorProps) => {
  const classes = useStyles();
  const newlyAddedLambdaRef = useRef<string | null>(null);
  const lambdaRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const nameInputRefs = useRef<Record<string, LambdaNameInputRef | null>>({});
  const lambdasRef = useRef(lambdas);
  lambdasRef.current = lambdas;

  const registerContainer = useCallback((name: string, el: HTMLDivElement | null) => {
    lambdaRefs.current[name] = el;
  }, []);

  const registerNameInput = useCallback((name: string, ref: LambdaNameInputRef | null) => {
    nameInputRefs.current[name] = ref;
  }, []);

  // Get defaults from props or use constants for addLambda
  const getDefaults = () => {
    const defaultSnapstart = isAuthorizer ? true : DEFAULT_SNAPSTART_ENABLED;
    return (
      propsDefaults || {
        runtime: DEFAULT_RUNTIME,
        architecture: DEFAULT_ARCHITECTURE,
        lambda_source: '',
        memory_size: DEFAULT_MEMORY,
        timeout: DEFAULT_TIMEOUT,
        snapstart: defaultSnapstart,
        env_vars: {},
      }
    );
  };

  const addLambda = () => {
    const defaults = getDefaults();
    const newLambdaName = `new_lambda_${Date.now()}`;
    const newLambdas = { ...lambdas };
    const newLambda: Lambda = {
      lambda_handler: '',
      lambda_source: defaults.lambda_source ?? '',
      architecture: defaults.architecture ?? DEFAULT_ARCHITECTURE,
      runtime: defaults.runtime ?? DEFAULT_RUNTIME,
      memory_size: defaults.memory_size ?? DEFAULT_MEMORY,
      timeout: defaults.timeout ?? DEFAULT_TIMEOUT,
      snapstart: defaults.snapstart ?? (isAuthorizer ? true : DEFAULT_SNAPSTART_ENABLED),
      // Delete the default iam_policies, since they are in the defaults object.
      iam_policy_names: [],
      iam_policy_names_custom: [],
    };
    // Only add env_vars if there are global env vars
    if (defaults.env_vars && Object.keys(defaults.env_vars).length > 0) {
      newLambda.env_vars = { ...defaults.env_vars };
    }

    newLambdas[newLambdaName] = newLambda;
    newlyAddedLambdaRef.current = newLambdaName;
    onChange(newLambdas);
  };

  // Scroll to newly added lambda and focus its name input
  useEffect(() => {
    if (newlyAddedLambdaRef.current) {
      const lambdaName = newlyAddedLambdaRef.current;
      const element = lambdaRefs.current[lambdaName];
      const nameInputRef = nameInputRefs.current[lambdaName];

      if (element && nameInputRef) {
        // Use requestAnimationFrame to ensure DOM updates are complete before scrolling/focusing
        requestAnimationFrame(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          nameInputRef.focus();
          newlyAddedLambdaRef.current = null;
        });
      }
    }
  }, [lambdas]);

  const deleteLambda = useCallback(
    (name: string) => {
      const current = lambdasRef.current;
      const newLambdas = { ...current };
      delete newLambdas[name];
      onChange(newLambdas);
    },
    [onChange],
  );

  const renameLambda = useCallback(
    (oldName: string, newName: string) => {
      if (oldName === newName || !newName.trim()) {
        return;
      }
      const current = lambdasRef.current;
      const newLambdas = { ...current };
      if (newLambdas[newName]) {
        return;
      }
      newLambdas[newName] = newLambdas[oldName];
      delete newLambdas[oldName];
      onChange(newLambdas);
    },
    [onChange],
  );

  const updateLambda = useCallback(
    (
      name: string,
      field: keyof Lambda,
      value: string | number | boolean | Record<string, string> | string[],
    ) => {
      const current = lambdasRef.current;
      const newLambdas = { ...current };
      if (!newLambdas[name]) {
        newLambdas[name] = {
          lambda_handler: '',
          lambda_source: '',
          architecture: DEFAULT_ARCHITECTURE,
          runtime: DEFAULT_RUNTIME,
          memory_size: DEFAULT_MEMORY,
          timeout: DEFAULT_TIMEOUT,
          snapstart: isAuthorizer ? true : DEFAULT_SNAPSTART_ENABLED,
        };
      }
      newLambdas[name] = {
        ...newLambdas[name],
        [field]: value,
      };
      onChange(newLambdas);
    },
    [onChange, isAuthorizer],
  );

  const content = (
    <>
      {title && (
        <Box className={classes.header}>
          <Typography variant="h6">{title}</Typography>
          {showLambdas && (
            <Button
              variant="contained"
              color="primary"
              onClick={addLambda}
              startIcon={<AddIcon />}
              style={{ backgroundColor: '#4caf50', color: '#FFFFFF' }}
            >
              Add Lambda
            </Button>
          )}
        </Box>
      )}
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          {description}
        </Typography>
      )}

      {/* Defaults Section */}
      {onDefaultsChange && (
        <LambdaDefaultsEditor
          defaults={propsDefaults}
          onChange={onDefaultsChange}
          isAuthorizer={isAuthorizer}
          iamPolicyNames={iamPolicyNames}
          customIamPolicyNames={customIamPolicyNames}
          onIamPolicyNamesChange={onIamPolicyNamesChange}
          onCustomIamPolicyNamesChange={onCustomIamPolicyNamesChange}
        />
      )}

      {/* Lambdas List */}
      {showLambdas && Object.keys(lambdas).length === 0 && emptyStateTitle && (
        <div className={classes.emptyState}>
          <Typography variant="body1">{emptyStateTitle}</Typography>
          {emptyStateDescription && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {emptyStateDescription}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addLambda}
            style={{
              marginTop: 16,
              backgroundColor: '#4caf50',
              color: '#FFFFFF',
            }}
          >
            {emptyStateAddButtonText}
          </Button>
        </div>
      )}
      {showLambdas &&
        Object.entries(lambdas).map(([name, lambda]) => (
          <LambdaListEditorRow
            key={name}
            name={name}
            lambda={lambda}
            defaults={propsDefaults}
            iamPolicyNames={iamPolicyNames}
            customIamPolicyNames={customIamPolicyNames}
            buildNumber={buildNumber}
            onUpdate={updateLambda}
            onRename={renameLambda}
            onDelete={deleteLambda}
            registerContainer={registerContainer}
            registerNameInput={registerNameInput}
          />
        ))}
    </>
  );

  if (showPaper) {
    return <Paper className={classes.section}>{content}</Paper>;
  }

  return <div>{content}</div>;
};

export const LambdaListEditor = memo(LambdaListEditorComponent);
